// Seeds the darwin_core read model after sign-in and clears it on sign-out.
//
// Renders nothing; it is a behavioural component mounted inside the store, auth
// and JSON:API providers. Two jobs:
//
//  1. Seed once per authenticated session. When the user is authenticated and the
//     REST client is ready, dispatch refreshHelloMessage(jsonApi, diagsApi) — the
//     darwin_core thunk that does jsonApi.findAll('home') and seeds homeList/zones
//     from REST. Live WebSocket frames keep that state current afterwards, so this
//     must fire exactly once per session, not on every render or WS reconnect.
//     The mobile app does the equivalent from its loading overlay effect.
//
//  2. Reset the store on de-authentication (SECURITY). Sign-out clears auth and
//     closes the socket but does not by itself wipe the cached device/zone/
//     schedule slices, so on a shared browser the next user could briefly see the
//     previous user's data. On an authenticated -> signed-out transition we
//     dispatch the root reset that re-initialises every slice, and re-arm the seed
//     guard so the next sign-in re-seeds.
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { actions } from 'darwin_core';

import { useAuth } from '../auth/AuthenticationContext.jsx';
import { useJsonApi } from '../api/JsonApiProvider.jsx';
import { useDiagnosticsJsonApi } from '../api/DiagnosticsJsonApiProvider.jsx';
import usePrevious from '../hooks/usePrevious.js';
import { clearSelectedHomeId } from '../session/selectedHome.js';
import { AUTH_RESET, AUTHENTICATED } from '../store.js';

export default function StoreBootstrap() {
  const dispatch = useDispatch();
  const { authenticated } = useAuth();
  const { jsonApi } = useJsonApi();
  const { diagsApi } = useDiagnosticsJsonApi();

  // Latched for the lifetime of one authenticated session; reset on sign-out.
  const seededRef = useRef(false);
  const wasAuthenticated = usePrevious(authenticated);

  // Open the store's write-guard the moment the user is authenticated — before
  // any read-model write below — so seed/live data is admitted. Defined ahead of
  // the seed effect so it runs first on the render where auth flips true.
  useEffect(() => {
    if (authenticated) {
      dispatch({ type: AUTHENTICATED });
    }
  }, [authenticated, dispatch]);

  // Seed the read model once, gated on the REST client being ready. diagsApi is
  // passed through best-effort (may still be undefined) — the thunk tolerates it.
  useEffect(() => {
    if (authenticated && jsonApi && !seededRef.current) {
      seededRef.current = true;
      dispatch(actions.refreshHelloMessage(jsonApi, diagsApi));
    }
  }, [authenticated, jsonApi, diagsApi, dispatch]);

  // Wipe cached state and re-arm the guard when the session ends. The reset also
  // closes the write-guard, so any REST/WS write still in flight is dropped.
  useEffect(() => {
    if (wasAuthenticated && !authenticated) {
      seededRef.current = false;
      // Clear the last-selected-home preference so the next user on a shared
      // browser does not boot into the previous user's home. (The home selector
      // in a later WP must ALSO validate any restored id against the signed-in
      // user's own homes before using it — this clear is belt-and-braces.)
      clearSelectedHomeId();
      dispatch({ type: AUTH_RESET });
    }
  }, [authenticated, wasAuthenticated, dispatch]);

  return null;
}
